import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { Sensor, SensorCategoryName } from '../../../shared/interfaces/sensor.interface';
import { OrganizationManageService } from '../../../core/services/organization-manage.service';

export interface SensorFormDialogData {
  sensor?: Sensor;
}

@Component({
  selector: 'app-sensor-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './sensor-form.component.scss',
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  template: `
    <h2 mat-dialog-title>{{ isEditMode ? 'センサーの編集' : '新規センサーの登録' }}</h2>
    <mat-dialog-content>
      <form [formGroup]="form" class="flex flex-col gap-4 pt-4">
        
        <mat-form-field appearance="outline" class="w-full">
          <mat-label>センサーID (DevEUIなど)</mat-label>
          <input matInput formControlName="id" placeholder="例: A840410000000000" [readonly]="isEditMode">
          @if (form.get('id')?.hasError('required') && form.get('id')?.touched) {
            <mat-error>センサーIDは必須です</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>所属組織</mat-label>
          <mat-select formControlName="organizationId" required>
            @for (org of organizationService.organizations(); track org.id) {
              <mat-option [value]="org.id">{{ org.name }}</mat-option>
            }
          </mat-select>
          @if (form.get('organizationId')?.hasError('required') && form.get('organizationId')?.touched) {
            <mat-error>所属組織は必須です</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="w-full">
          <mat-label>センサーカテゴリー</mat-label>
          <mat-select formControlName="categoryName" required>
            @for (cat of categories; track cat) {
              <mat-option [value]="cat">{{ cat }}</mat-option>
            }
          </mat-select>
          @if (form.get('categoryName')?.hasError('required') && form.get('categoryName')?.touched) {
            <mat-error>センサーカテゴリーは必須です</mat-error>
          }
        </mat-form-field>

      </form>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button matButton (click)="onCancel()">キャンセル</button>
      <button matButton="tonal" color="primary" [disabled]="form.invalid || isLoading()" (click)="onSubmit()">
        保存
      </button>
    </mat-dialog-actions>
  `
})
export class SensorFormComponent {
  private readonly dialogRef = inject(MatDialogRef<SensorFormComponent>);
  private readonly data = inject<SensorFormDialogData>(MAT_DIALOG_DATA, { optional: true });
  private readonly fb = inject(FormBuilder);
  public readonly organizationService = inject(OrganizationManageService);

  public readonly isEditMode = !!this.data?.sensor;
  public readonly isLoading = signal(false);

  public readonly categories: SensorCategoryName[] = ['WBGT', 'WIND', 'NOISE', 'PH', 'TILT'];

  public readonly form: FormGroup = this.fb.group({
    id: [{ value: this.data?.sensor?.id || '', disabled: this.isEditMode }, [Validators.required]],
    organizationId: [this.data?.sensor?.organizationId || '', [Validators.required]],
    categoryName: [this.data?.sensor?.categoryName || '', [Validators.required]],
  });

  public onSubmit(): void {
    if (this.form.invalid) return;

    this.isLoading.set(true);
    try {
      // Use getRawValue to get 'id' even if it's disabled
      const formValue = this.form.getRawValue();

      const result = {
        id: formValue.id,
        organizationId: formValue.organizationId,
        categoryName: formValue.categoryName,
      };

      this.dialogRef.close(result);
    } finally {
      this.isLoading.set(false);
    }
  }

  public onCancel(): void {
    this.dialogRef.close();
  }
}
